import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../config/API";

function UserDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // =====================================================
  // USER FROM LOCAL STORAGE
  // =====================================================

  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");

      return storedUser ? JSON.parse(storedUser) : {};
    } catch (error) {
      console.error("Invalid user data in localStorage", error);
      return {};
    }
  });

  // =====================================================
  // PROFILE DATA
  // =====================================================

  const [profileData, setProfileData] = useState(null);

  const [profileLoading, setProfileLoading] = useState(true);

  const [profileError, setProfileError] = useState("");

  // =====================================================
  // DATA FROM PAYMENT SUCCESS / ROUTE STATE
  // =====================================================

  const navigationState = location.state || {};

  const [userRegisterId, setUserRegisterId] = useState(
    navigationState.userRegisterId || null,
  );

  const [mobileNumber, setMobileNumber] = useState(
    navigationState.mobileNumber || "",
  );

  // =====================================================
  // GET REGISTER ID + MOBILE NUMBER
  // =====================================================

  useEffect(() => {
    let registerId = navigationState.userRegisterId || null;

    let mobile = navigationState.mobileNumber || "";

    // -----------------------------------------------
    // Use localStorage first
    // -----------------------------------------------

    if (!registerId) {
      const storedRegisterId = localStorage.getItem("userRegisterId");

      if (storedRegisterId) {
        registerId = Number(storedRegisterId);
      }
    }

    // -----------------------------------------------
    // If route state missing, use sessionStorage
    // -----------------------------------------------

    if (!registerId) {
      const storedRegisterId = sessionStorage.getItem("userRegisterId");

      if (storedRegisterId) {
        registerId = Number(storedRegisterId);
      }
    }

    if (!mobile) {
      const storedMobile = sessionStorage.getItem("mobileNumber");

      if (storedMobile) {
        mobile = storedMobile;
      }
    }

    // -----------------------------------------------
    // If still missing, use localStorage user
    // -----------------------------------------------

    if (!registerId) {
      registerId =
        user?.userRegisterId || user?.registerId || user?.userId || null;
    }

    if (!mobile) {
      mobile = user?.mobileNumber || user?.contactNo || "";
    }

    // -----------------------------------------------
    // Update state
    // -----------------------------------------------

    if (registerId) {
      setUserRegisterId(Number(registerId));

      localStorage.setItem("userRegisterId", String(registerId));

      sessionStorage.setItem("userRegisterId", String(registerId));
    }

    if (mobile) {
      setMobileNumber(String(mobile));

      sessionStorage.setItem("mobileNumber", String(mobile));
    }

    console.log("User Dashboard Data");
    console.log("userRegisterId:", registerId);
    console.log("mobileNumber:", mobile);
  }, [navigationState.userRegisterId, navigationState.mobileNumber, user]);

  // =====================================================
  // GET USER PROFILE DATA
  // =====================================================

  useEffect(() => {
    if (!userRegisterId) {
      return;
    }

    const fetchUserProfile = async () => {
      try {
        setProfileLoading(true);
        setProfileError("");

        console.log("Fetching profile for userRegisterId:", userRegisterId);

        const response = await axios.get(
          `${API.V2}/usersDashboard/getUserProfileDataByUserRegisterId`,
          {
            params: {
              userRegisterId: userRegisterId,
            },
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        console.log("User Profile Response:", response.data);

        if (response.data?.statusCode === 200 && response.data?.data) {
          const profile = response.data.data;

          setProfileData(profile);

          // -----------------------------------------
          // Update localStorage user data
          // -----------------------------------------

          const updatedUser = {
            ...user,
            userRegisterId: profile.userRegisterId,
            firstName: profile.firstName,
            lastName: profile.lastName,
            contactNo: profile.contactNo,
            emailId: profile.emailId,
            gender: profile.gender,
            profileUrl: profile.profileUrl,
          };

          localStorage.setItem("user", JSON.stringify(updatedUser));

          setUser(updatedUser);

          // Update mobile number
          if (profile.contactNo) {
            setMobileNumber(String(profile.contactNo));

            sessionStorage.setItem("mobileNumber", String(profile.contactNo));
          }
        } else {
          setProfileError(
            response.data?.message || "Unable to fetch profile data.",
          );
        }
      } catch (error) {
        console.error("Get User Profile Error:", error);

        setProfileError(
          error.response?.data?.message || "Unable to fetch profile data.",
        );
      } finally {
        setProfileLoading(false);
      }
    };

    fetchUserProfile();
  }, [userRegisterId]);

  // =====================================================
  // COMMON NAVIGATION STATE
  // =====================================================

  const dashboardState = {
    userRegisterId: userRegisterId ? Number(userRegisterId) : null,

    mobileNumber: mobileNumber || "",
  };

  // =====================================================
  // BED DETAILS
  // =====================================================

  const handleBedDetails = () => {
    if (!userRegisterId) {
      alert("User registration ID not found.");
      return;
    }

    navigate("/userBedDetails", {
      // state: dashboardState,
    });
  };

  // =====================================================
  // PROFILE PICTURE
  // =====================================================

  const profileImage =
    profileData?.profileUrl && profileData.profileUrl.trim() !== ""
      ? profileData.profileUrl
      : null;

  const profileInitial = profileData?.firstName
    ? profileData.firstName.charAt(0).toUpperCase()
    : "U";

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div style={styles.page}>
      {/* ================= HEADER ================= */}

      <div style={styles.header}>
        <div style={styles.profileSection}>
          {/* ================= PROFILE IMAGE ================= */}

          <div style={styles.avatar}>
            {profileLoading ? (
              <span style={styles.avatarLoading}>...</span>
            ) : profileImage ? (
              <img
                src={profileImage}
                alt="User Profile"
                style={styles.profileImage}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextSibling.style.display = "flex";
                }}
              />
            ) : null}

            {/* Fallback Initial */}

            <span
              style={{
                ...styles.avatarFallback,
                display: profileImage && !profileLoading ? "none" : "flex",
              }}
            >
              {profileInitial}
            </span>
          </div>

          {/* ================= USER INFORMATION ================= */}

          <div>
            <h2 style={styles.userName}>
              Welcome,
              {profileData?.firstName ? ` ${profileData.firstName}` : " User"}
            </h2>

            <p style={styles.email}>
              {profileData?.emailId || user?.emailId || ""}
            </p>

            {/* {userRegisterId && (
              <p style={styles.registerId}>Registration ID: {userRegisterId}</p>
            )} */}

            {mobileNumber && (
              <p style={styles.mobileNumber}>📱 {mobileNumber}</p>
            )}
          </div>
        </div>

        {/* ================= PROFILE BUTTON ================= */}

        <div style={styles.headerRight}>
          <button
            style={styles.profileBtn}
            onClick={() =>
              navigate("/userProfile", {
                state: dashboardState,
              })
            }
          >
            👤 My Profile
          </button>
        </div>
      </div>

      {/* ================= PROFILE ERROR ================= */}

      {profileError && <div style={styles.profileError}>{profileError}</div>}

      {/* ================= DASHBOARD ================= */}

      <h2 style={styles.dashboardTitle}>User Dashboard</h2>

      <div style={styles.grid}>
        {/* ================= BED DETAILS ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #2563eb",
          }}
          onClick={handleBedDetails}
        >
          <div style={styles.icon}>🛏️</div>

          <h3 style={styles.cardTitle}>Bed Details</h3>

          <p style={styles.cardDesc}>
            View Floor, Room, Bed, Joining Date & Stay Type.
          </p>
        </div>

        {/* ================= PAYMENT ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #16a34a",
          }}
          onClick={() =>
            navigate("/userPayments", {
              state: dashboardState,
            })
          }
        >
          <div style={styles.icon}>💳</div>

          <h3 style={styles.cardTitle}>Payments</h3>

          <p style={styles.cardDesc}>
            View Payment History, Current Bill & Receipts.
          </p>
        </div>

        {/* ================= COMPLAINTS ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #dc2626",
          }}
          onClick={() =>
            navigate("/complaints", {
              state: dashboardState,
            })
          }
        >
          <div style={styles.icon}>🎫</div>

          <h3 style={styles.cardTitle}>Complaints</h3>

          <p style={styles.cardDesc}>
            Raise Complaint & Track Complaint Status.
          </p>
        </div>

        {/* ================= REFERRALS ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #f59e0b",
          }}
          onClick={() =>
            navigate("/referrals", {
              state: dashboardState,
            })
          }
        >
          <div style={styles.icon}>👥</div>

          <h3 style={styles.cardTitle}>Referrals</h3>

          <p style={styles.cardDesc}>
            Invite your friends, earn referral rewards and track referral
            history.
          </p>
        </div>

        {/* ================= FOOD MENU ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #10b981",
          }}
          onClick={() =>
            navigate("/foodMenu", {
              state: dashboardState,
            })
          }
        >
          <div style={styles.icon}>🍽️</div>

          <h3 style={styles.cardTitle}>Food Menu</h3>

          <p style={styles.cardDesc}>
            View today's breakfast, lunch, dinner and weekly food menu.
          </p>
        </div>

        {/* ================= NOTICES ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #6366f1",
          }}
          onClick={() =>
            navigate("/notices", {
              state: dashboardState,
            })
          }
        >
          <div style={styles.icon}>📢</div>

          <h3 style={styles.cardTitle}>Notices</h3>

          <p style={styles.cardDesc}>
            Latest announcements, maintenance updates and events.
          </p>
        </div>

        {/* ================= RULES ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #8b5cf6",
          }}
          onClick={() =>
            navigate("/rules", {
              state: dashboardState,
            })
          }
        >
          <div style={styles.icon}>📜</div>

          <h3 style={styles.cardTitle}>Rules & Regulations</h3>

          <p style={styles.cardDesc}>
            Read PG rules, timings, visitor policy and regulations.
          </p>
        </div>

        {/* ================= SUPPORT ================= */}

        <div
          style={{
            ...styles.card,
            borderTop: "5px solid #06b6d4",
          }}
          onClick={() =>
            navigate("/support", {
              state: dashboardState,
            })
          }
        >
          <div style={styles.icon}>📞</div>

          <h3 style={styles.cardTitle}>Support</h3>

          <p style={styles.cardDesc}>
            Contact PG management for assistance and emergency help.
          </p>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;

/* =====================================================
   STYLES
===================================================== */

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    padding: "30px",
    fontFamily: "'Segoe UI', sans-serif",
  },

  header: {
    background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
    borderRadius: "18px",
    padding: "25px 35px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,.15)",
  },

  profileSection: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },

  avatar: {
    width: "75px",
    height: "75px",
    borderRadius: "50%",
    background: "#fff",
    overflow: "hidden",
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(0,0,0,.15)",
  },

  profileImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  avatarFallback: {
    position: "absolute",
    inset: 0,
    justifyContent: "center",
    alignItems: "center",
    color: "#2563eb",
    fontWeight: "700",
    fontSize: "32px",
    background: "#fff",
  },

  avatarLoading: {
    color: "#2563eb",
    fontSize: "18px",
    fontWeight: "700",
  },

  userName: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
  },

  email: {
    marginTop: "8px",
    marginBottom: 0,
    opacity: 0.9,
    fontSize: "15px",
  },

  registerId: {
    marginTop: "5px",
    marginBottom: 0,
    opacity: 0.85,
    fontSize: "13px",
  },

  mobileNumber: {
    marginTop: "4px",
    marginBottom: 0,
    opacity: 0.85,
    fontSize: "13px",
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
  },

  profileBtn: {
    background: "#fff",
    color: "#2563eb",
    border: "none",
    padding: "12px 24px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "15px",
  },

  profileError: {
    marginTop: "15px",
    padding: "12px 16px",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: "10px",
    fontSize: "14px",
  },

  dashboardTitle: {
    marginTop: "35px",
    marginBottom: "25px",
    color: "#0f172a",
    fontSize: "30px",
    fontWeight: "700",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
    gap: "25px",
  },

  card: {
    background: "#fff",
    borderRadius: "18px",
    padding: "30px",
    cursor: "pointer",
    transition: "transform .2s ease, box-shadow .2s ease",
    boxShadow: "0 8px 25px rgba(0,0,0,.08)",
  },

  icon: {
    fontSize: "55px",
    marginBottom: "18px",
  },

  cardTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: "700",
  },

  cardDesc: {
    marginTop: "12px",
    color: "#64748b",
    lineHeight: "24px",
    fontSize: "15px",
  },
};
